// GB #1435 (D4): AuthenticationForm renders on both /login and /signup. Its
// landing effect used to send a FULLY authenticated user (has a user AND a
// userDoc with a username) to the login form (Tab 2) and make them re-enter
// their credentials. Every stray landing on /login or /signup (middleware
// bounces, client redirects) thus became a forced manual re-login.
//
// This is the pure decision core of that effect, extracted so every branch is
// testable without mounting the component or mocking Firebase/router. The
// component (AuthenticationForm.tsx) applies the returned action; it owns the
// side effects (token refresh, navigation, tab/isReady state).

export type AuthLandingAction =
  // userDoc still loading for a signed-in user — do nothing, keep the spinner.
  | 'wait'
  // GB #1534 Phase 2: signed in, userDoc never arrived, and the listener's
  // retry budget is exhausted (AuthContext's userDocError). isLoadingUserDoc
  // stays true forever in this case — without this branch 'wait' would too,
  // stranding the user on the Tab-0 spinner permanently. Show a terminal
  // state with a manual retry instead.
  | 'error'
  // User manually selected a non-login tab (signup/passwordless/profile) — do
  // not override it; just reveal the current tab.
  | 'respect-manual'
  // Show the password login form (Tab 2).
  | 'login-tab'
  // Signed in but no username yet — needs profile setup (Tab 4). Ghost-account
  // path; GB #971 depends on it.
  | 'profile-setup'
  // Signed in with a complete profile, sitting on /login or /signup — forward
  // them into the app instead of showing the login form.
  | 'forward';

// Firebase Auth resolves in three stages: `undefined` (not yet resolved on a
// hard load), `null` (resolved, no user — guest), or a User. Collapsing
// undefined into "no user" would flash an interactive login form under an
// authenticated user during the brief unresolved window on a hard /login load.
export type AuthState = 'pending' | 'authed' | 'guest';

export interface AuthLandingInput {
  authState: AuthState;
  isLoadingUserDoc: boolean;
  // GB #1534 Phase 2: AuthContext's userDocError — true once the user-doc
  // listener's retry budget is exhausted with no doc ever delivered.
  userDocError: boolean;
  hasUsername: boolean;
  pathname: string;
  tabIndex: number;
  alreadyForwarded: boolean;
}

// Max length for an accepted ?redirect= target. Anything longer is not a real
// in-app path — reject rather than navigate to it.
const MAX_REDIRECT_LEN = 2048;

// Control characters (0x00-0x1F, 0x7F) in a redirect target are an attack
// vector — URL parsing can strip a tab/newline to turn '/\t/evil.com' into a
// protocol-relative '//evil.com' — never legitimate input. Written with
// \u escapes (not a raw-byte character class) so the source stays ASCII-clean.
const CONTROL_CHARS = /[\u0000-\u001f\u007f]/;

// AuthenticationForm is only mounted by /login and /signup today, but it may be
// reused elsewhere someday. The forward only fires on these two paths so a
// future reuse keeps the original login-form behavior rather than silently
// redirecting.
// Where a fully-authenticated user lands when forwarded off /login or /signup
// with no (or an unsafe) ?redirect= target. Shared by the client forward
// (AuthenticationForm) and the server-side edge forward (middleware, GB #1463)
// so the two layers can never drift to different destinations.
export const DEFAULT_FORWARD_DEST = '/dashboard/account-overview';

export function isForwardEligiblePath(pathname: string): boolean {
  return pathname === '/login' || pathname === '/signup';
}

// GB #677: the recovery route is never a legitimate DESTINATION. Forwarding a
// user to it would hand them a spinner that immediately forwards again, and a
// crafted nested ?redirect= chain could peel one full page load per level.
const NON_DESTINATION_PATHS = ['/login', '/signup', '/auth/recover'];

// Open-redirect guard for ?redirect=. Accepts only a site-relative path: a
// single leading '/', no protocol, no protocol-relative '//', no backslash
// (browsers can normalize '\' to '/'), and no control characters.
export function isSafeRedirect(value: unknown): value is string {
  if (typeof value !== 'string') return false;
  if (value.length === 0) return false;
  if (value.length > MAX_REDIRECT_LEN) return false; // absurdly long → not a real path
  if (value[0] !== '/') return false; // rejects '', 'https://...', 'javascript:...'
  if (value[1] === '/') return false; // rejects protocol-relative '//evil.com'
  if (value.includes('\\')) return false; // rejects '/\evil' and any backslash trick
  if (CONTROL_CHARS.test(value)) return false;
  return true;
}

// Resolves where a forwarded user actually lands. Falls back to defaultDest
// when ?redirect= is missing/unsafe, AND when it points back at an auth page
// (a crafted /login?redirect=/login must not bounce the user straight back
// here). Query/hash are stripped before the auth-page check.
export function resolveForwardDest(redirectParam: unknown, defaultDest: string): string {
  if (!isSafeRedirect(redirectParam)) return defaultDest;
  // Strip query/hash AND any trailing slashes before the auth-page check so a
  // crafted '/login/' or '/signup/?x=1' also defaults out (they normalize back
  // to an auth page).
  const pathOnly = redirectParam.split(/[?#]/)[0].replace(/\/+$/, '');
  if (NON_DESTINATION_PATHS.includes(pathOnly)) return defaultDest;
  return redirectParam;
}

// --- Cross-navigation forward loop cap (HIGH, adversarial review) -----------
// If the auth backend degrades (e.g. /api/auth fast-fails non-200), middleware
// can persistently bounce the forward destination back to /login. Each bounce
// remounts AuthenticationForm, resetting the per-mount hasForwardedRef — a
// redirect storm. This per-TAB cap (sessionStorage, self-expiring 60s window)
// breaks that storm by falling back to the login form after a couple of tries.
export const FORWARD_CAP_KEY = 'gf_authed_forward';
export const FORWARD_CAP_WINDOW_MS = 60_000;
export const FORWARD_CAP_MAX = 2;

export interface ForwardAttempt {
  count: number;
  ts: number; // epoch ms of the FIRST attempt in the current burst (anchors the window)
}

// Pure decision: given the prior recorded attempt (or null) and the current
// time, decide whether this forward is capped and what record to persist next.
// The window is anchored to the first attempt's ts so a burst can't be extended
// indefinitely by its own retries.
export function evaluateForwardCap(
  prev: ForwardAttempt | null,
  now: number,
  windowMs: number = FORWARD_CAP_WINDOW_MS,
  maxAttempts: number = FORWARD_CAP_MAX
): { capped: boolean; next: ForwardAttempt } {
  if (prev && now - prev.ts < windowMs) {
    if (prev.count >= maxAttempts) {
      return { capped: true, next: prev }; // capped — leave the record to self-expire
    }
    return { capped: false, next: { count: prev.count + 1, ts: prev.ts } };
  }
  // No prior record, or the previous burst's window has expired → fresh burst.
  return { capped: false, next: { count: 1, ts: now } };
}

function parseForwardAttempt(raw: string | null): ForwardAttempt | null {
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (
      parsed &&
      typeof parsed === 'object' &&
      typeof (parsed as ForwardAttempt).count === 'number' &&
      typeof (parsed as ForwardAttempt).ts === 'number'
    ) {
      return parsed as ForwardAttempt;
    }
  } catch {
    // malformed / tampered — treat as no record
  }
  return null;
}

// Reads the per-tab attempt record, evaluates the cap, and persists the next
// record when NOT capped. Fails OPEN (capped: false) on any storage error
// (e.g. Safari private mode throws on sessionStorage) — the cap is a safety
// net, never a gate that could block a legitimate forward.
export function bumpForwardCap(now: number = Date.now()): { capped: boolean } {
  try {
    const prev = parseForwardAttempt(sessionStorage.getItem(FORWARD_CAP_KEY));
    const { capped, next } = evaluateForwardCap(prev, now);
    if (!capped) {
      sessionStorage.setItem(FORWARD_CAP_KEY, JSON.stringify(next));
    }
    return { capped };
  } catch {
    return { capped: false };
  }
}

export function resolveAuthLandingAction(input: AuthLandingInput): AuthLandingAction {
  const { authState, isLoadingUserDoc, userDocError, hasUsername, pathname, tabIndex, alreadyForwarded } = input;

  // Auth not yet resolved (undefined) — keep the spinner. Firebase always fires
  // onIdTokenChanged (null for guests, a User otherwise), so this terminates.
  // Without this, a hard load of /login would flash the login form under an
  // authenticated user during the unresolved window.
  if (authState === 'pending') {
    return 'wait';
  }

  // Wait for userDoc to load before making tab decisions (prevents the race
  // where we go to Tab 4 before userDoc loads) — UNLESS the listener's retry
  // budget is exhausted (GB #1534 Phase 2). isLoadingUserDoc alone never
  // resolves in that case, so 'wait' alone would strand the user on the
  // Tab-0 spinner forever; userDocError is the signal that stops the wait.
  if (authState === 'authed' && isLoadingUserDoc) {
    return userDocError ? 'error' : 'wait';
  }

  // Don't override a manual tab selection. Only auto-decide on initial load
  // (tab 0) or when already on the login tab (tab 2).
  if (tabIndex !== 0 && tabIndex !== 2) {
    return 'respect-manual';
  }

  // Guest (resolved, no user) — show the login form.
  if (authState === 'guest') {
    return 'login-tab';
  }

  // Signed in and userDoc finished loading.
  if (hasUsername) {
    if (isForwardEligiblePath(pathname)) {
      // Already kicked off the forward — keep the spinner up while navigation
      // is in flight; never fall through to 'login-tab' (that would flash the
      // login form under a user who is on their way out).
      return alreadyForwarded ? 'wait' : 'forward';
    }
    // Reused on some other page someday: preserve the original behavior.
    return 'login-tab';
  }

  // Signed in but no username — needs profile setup.
  return 'profile-setup';
}
